import { useEffect, useState } from "react";
import { PublicClientApplication } from "@azure/msal-browser";

/**
 * This component runs in the Teams-managed popup window.
 * It initiates the MSAL login redirect to Azure B2C.
 * After successful login, the user is redirected to /auth-end,
 * which completes the authentication process.
 *
 * Security: clientId/authority/apiScope from the query string are validated against
 * server-provided configuration (/frontend_settings) before use, preventing open
 * redirect abuse via crafted /auth-start URLs.
 */
export function AuthStart() {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const startAuth = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const clientId = params.get("clientId");
        const authority = params.get("authority");
        const apiScope = params.get("apiScope");

        if (!clientId || !authority || !apiScope) {
          setError("Missing required parameters");
          return;
        }

        // Validate params against server config to prevent open redirect abuse.
        const settings = await fetch("/frontend_settings").then(r => r.json()).catch(() => null);
        const branding = settings?.branding;
        const expectedClientId = branding?.azure_b2c_client_id;
        const expectedAuthority = branding?.azure_b2c_userflow_endpoint;
        const b2cTenantName = branding?.b2c_tenant_subdomain || "";
        const expectedApiScope = b2cTenantName
          ? `https://${b2cTenantName}.onmicrosoft.com/${expectedClientId}/access_as_user`
          : `api://${expectedClientId}/access_as_user`;

        if (
          !expectedClientId ||
          !expectedAuthority ||
          clientId !== expectedClientId ||
          authority !== expectedAuthority ||
          apiScope !== expectedApiScope
        ) {
          setError("Invalid authentication parameters");
          return;
        }

        // Store config for AuthEnd to retrieve after B2C redirect
        localStorage.setItem("teams.auth.config", JSON.stringify({ clientId, authority }));

        const msalInstance = new PublicClientApplication({
          auth: {
            clientId,
            authority,
            redirectUri: `${window.location.origin}/auth-end`,
            knownAuthorities: [new URL(authority).hostname],
          },
          cache: {
            cacheLocation: "localStorage",
            storeAuthStateInCookie: false,
          },
        });

        await msalInstance.initialize();
        await msalInstance.loginRedirect({
          scopes: ["openid", "profile", apiScope],
          prompt: "select_account"
        });
      } catch (err) {
        console.error("AuthStart error:", err);
        setError(err instanceof Error ? err.message : "Authentication failed");
      }
    };

    startAuth();
  }, []);

  return (
    <div style={{ padding: "20px", textAlign: "center" }}>
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : (
        <p>Redirecting to login...</p>
      )}
    </div>
  );
}

export default AuthStart;